// Copyright (c) Docugami, Inc. All rights reserved.

import { Point } from "sdv/Types";

export interface FetchState {
    fetching: boolean;
    fetched: boolean;
}

export interface Box {
    left: number;
    top: number;
    right: number;
    bottom: number;
    page: number;
}

export function combineBoxes(boxes: Box[]): Box {
    if (boxes.length === 0) {
        return makeInvalidBox();
    }

    return {
        left: Math.min(...boxes.map(b => b.left)),
        top: Math.min(...boxes.map(b => b.top)),
        right: Math.max(...boxes.map(b => b.right)),
        bottom: Math.max(...boxes.map(b => b.bottom)),
        page: -1
    }
}

export function pointInBox(x: number, y: number, box: Box, top?: number, right?: number, bottom?: number, left?: number): boolean {
    top = top ?? 0;
    right = right ?? top;
    left = left ?? right;
    bottom = bottom ?? top

    return x >= box.left - left &&
        x <= box.right + right &&
        y >= box.top - top &&
        y <= box.bottom + bottom;
}

export function growBox(box: Box, top: number, right?: number, bottom?: number, left?: number): Box {
    right = right ?? top;
    left = left ?? right;
    bottom = bottom ?? top
    return {
        ...box,
        left: box.left - left,
        top: box.top - top,
        right: box.right + right,
        bottom: box.bottom + bottom,
    }
}

export function boxWidth(box: Box): number {
    return box.right - box.left;
}

export function boxHeight(box: Box): number {
    return box.bottom - box.top;
}

export function boxCenterX(box: Box): number {
    return box.left + boxWidth(box) / 2
}

export function boxCenterY(box: Box): number {
    return box.top + boxHeight(box) / 2;
}

export function boxCenter(box: Box): Point {
    return { x: boxCenterX(box), y: boxCenterY(box) }
}

export function makeBoxFromCenter(center: Point, height: number, width: number, page: number): Box {
    return {
        top: center.y - height / 2,
        bottom: center.y + height / 2,
        left: center.x - width / 2,
        right: center.x + width / 2,
        page: page
    }
}

export function scaleBox(box: Box, scaleFactor: number): Box {
    const width = (box.right - box.left) * scaleFactor;
    const height = (box.bottom - box.top) * scaleFactor;
    return {
        ...box,
        left: box.left - width / 2,
        top: box.top - height / 2,
        right: box.right + width / 2,
        bottom: box.bottom + height / 2,
    }
}

export function pointInBoxes(x: number, y: number, boxes: Box[]): boolean {
    for (const box of boxes) {
        if (pointInBox(x, y, box)) {
            return true;
        }
    }

    return false;
}

export function areaOfBox(box: Box): number {
    return (box.right - box.left) * (box.bottom - box.top);
}

export function areaOfBoxes(boxes: Box[]): number {
    let area = 0;
    for (const box of boxes) {
        area += areaOfBox(box);
    }
    return area;
}

export function makeInvalidBox(): Box {
    return { left: -1, top: -1, right: -1, bottom: -1, page: -1 };
}

export function isInvalidBox(box: Box): boolean {
    return box.left === -1 &&
        box.top === -1 &&
        box.right === -1 &&
        box.bottom === -1 &&
        box.page === -1;
}

export function boxesIntersect(box1: Box, box2: Box): boolean {
    return boxesIntersectX(box1, box2) && boxesIntersectY(box1, box2);
}

export function boxesIntersectX(box1: Box, box2: Box): boolean {
    return Math.max(box1.left, box2.left) < Math.min(box1.right, box2.right);
}

export function boxesIntersectY(box1: Box, box2: Box): boolean {
    return Math.max(box1.top, box2.top) < Math.min(box1.bottom, box2.bottom);
}

export function distanceFromBox(box: Box, x: number, y: number) {
    return Math.sqrt(distanceFromBoxSquared(box, x, y));
}

export function distanceFromBoxSquared(box: Box, x: number, y: number) {
    var dx = distanceFromBoxXPlane(box, x);
    var dy = distanceFromBoxYPlane(box, y);
    return dx * dx + dy * dy;
}

export function distanceFromBoxXPlane(box: Box, x: number): number {
    var dx = Math.max(box.left - x, 0, x - box.right);
    return dx;
}

export function distanceFromBoxYPlane(box: Box, y: number): number {
    var dy = Math.max(box.top - y, 0, y - box.bottom);
    return dy;
}

export function boxesEqual(b1?: Box, b2?: Box): boolean {
    if (b1 === b2) {
        return true;
    }

    return b1?.bottom === b2?.bottom &&
        b1?.left === b2?.left &&
        b1?.right === b2?.right &&
        b1?.top === b2?.top &&
        b1?.page === b2?.page;
}

export function nestedBox(b1?: Box, b2?: Box): boolean {
    if (!b1 || !b2 || b1 === b2) {
        return false;
    }

    return !boxesEqual(b1, b2) &&
        b1.bottom <= b2.bottom &&
        b1.left >= b2.left &&
        b1.right <= b2.right &&
        b1.top >= b2.top &&
        b1.page === b2.page;
}

export function getRelativeBox(box: Box, containingBox: Box): Box {
    return {
        left: box.left - containingBox.left,
        top: box.top - containingBox.top,
        right: box.right - containingBox.left,
        bottom: box.bottom - containingBox.top,
        page: box.page
    }
}

export const getOverlappingEditTokens = (token: number, tokens: TokenIdToBox): number[] | undefined => {
    let editTokens: number[] | undefined;
    const currentBox = tokens[token];
    if (!!currentBox) {
        let editToken = token - 1;
        while (editToken >= 0 && boxesEqual(currentBox, tokens[editToken])) {
            editTokens = editTokens || [];
            editTokens.push(editToken);
            editToken--;
        }

        editToken = token + 1;
        while (boxesEqual(currentBox, tokens[editToken])) {
            editTokens = editTokens || [];
            editTokens.push(editToken);
            editToken++;
        }
    }

    return !!editTokens ? editTokens.sort() : undefined;
}

export interface TokenIdToBox {
    [tokenId: number]: Box;
}
